import { computed, ref, type Ref } from 'vue'

interface ScriptImportFormShape {
  scriptBody: string
  scriptTitle: string
}

interface UseScriptImportInputsOptions {
  formData: ScriptImportFormShape
  scriptUploadInput: Ref<HTMLInputElement | null>
  success: (message: string) => void
  error: (message: string) => void
}

export function useScriptImportInputs(options: UseScriptImportInputsOptions) {
  const scriptImportSource = ref<'manual' | 'file'>('manual')
  const scriptImportFileName = ref('')

  const scriptBodyLength = computed(() => options.formData.scriptBody.trim().length)
  const scriptImportSourceLabel = computed(() => {
    if (scriptImportSource.value === 'file' && scriptImportFileName.value) {
      return `文件导入 · ${scriptImportFileName.value}`
    }
    return '手动粘贴'
  })

  const scriptImportHint = computed(() => {
    if (!options.formData.scriptBody.trim()) {
      return '导入或粘贴剧本后，系统会把它直接挂到剧本审批节点，你可以先人工修改，再决定是否通过进入分镜。'
    }
    return '创建后会直接打开剧本审批工作台；你可以在同一页继续改稿、退回重做，或确认进入分镜阶段。'
  })

  function triggerScriptUpload() {
    if (options.scriptUploadInput.value) {
      options.scriptUploadInput.value.value = ''
      options.scriptUploadInput.value.click()
    }
  }

  function clearImportedScript() {
    options.formData.scriptBody = ''
    scriptImportSource.value = 'manual'
    scriptImportFileName.value = ''
  }

  async function handleScriptFileSelected(event: Event) {
    const target = event.target as HTMLInputElement | null
    const file = target?.files?.[0]
    if (!file) {
      return
    }

    try {
      const content = await file.text()
      options.formData.scriptBody = content
      if (!options.formData.scriptTitle.trim()) {
        options.formData.scriptTitle = file.name.replace(/\.[^.]+$/, '')
      }
      scriptImportSource.value = 'file'
      scriptImportFileName.value = file.name
      options.success('剧本文件已导入，可以继续创建项目')
    } catch {
      options.error('读取剧本文件失败，请重试或直接粘贴文本')
    } finally {
      if (target) {
        target.value = ''
      }
    }
  }

  return {
    clearImportedScript,
    handleScriptFileSelected,
    scriptBodyLength,
    scriptImportHint,
    scriptImportSource,
    scriptImportSourceLabel,
    triggerScriptUpload,
  }
}